import React from "react";

export function Card({ children, className = "", testId, featured = false, glass = false, accent, ...rest }) {
  // Optional glassmorphism variant — frosted white panel with a subtle
  // top-edge highlight strip. `accent` (hex) tints the border + adds a soft
  // outer glow so the card picks up the section's identity color.
  if (glass) {
    const border = accent ? `${accent}30` : "rgba(255,255,255,0.08)";
    const glowStop = accent ? `${accent}66` : "transparent";
    return (
      <div
        data-testid={testId}
        className={`relative rounded-2xl p-6 bg-white/[0.03] backdrop-blur-2xl backdrop-saturate-150 border overflow-hidden transition-all duration-500 hover:border-white/[0.14] ${className}`}
        style={{ borderColor: border, boxShadow: `0 1px 0 rgba(255,255,255,0.05) inset, 0 20px 40px -20px rgba(0,0,0,0.5), 0 0 60px -30px ${glowStop}` }}
        {...rest}
      >
        {accent && (
          <span
            aria-hidden="true"
            className="absolute top-0 left-6 right-6 h-px pointer-events-none"
            style={{ background: `linear-gradient(90deg, transparent, ${accent}88, transparent)` }}
          />
        )}
        {children}
      </div>
    );
  }
  return (
    <div data-testid={testId} className={`card ${featured ? "card-featured" : ""} ${className}`} {...rest}>
      {children}
    </div>
  );
}

export function Badge({ children, tone = "gold", pulse = false }) {
  const map = {
    gold: "chip",
    neutral: "chip chip-neutral",
    danger: "chip chip-danger",
    avail: "chip chip-avail",
    // legacy mappings so existing pages keep rendering
    cyan: "chip",
    orange: "chip",
    red: "chip chip-danger",
    green: "chip chip-avail",
    slate: "chip chip-neutral",
  };
  const dot = tone === "danger" || tone === "red" ? "bg-aog" : tone === "avail" || tone === "green" ? "bg-avail" : "bg-champagne";
  return (
    <span className={map[tone] || "chip"}>
      {pulse && <span className={`h-1.5 w-1.5 rounded-full ${dot} animate-pulse`} />}
      {children}
    </span>
  );
}

export function Field({ label, children, helper }) {
  return (
    <label className="block">
      <div className="eyebrow-muted mb-2.5">{label}</div>
      {children}
      {helper && <div className="mt-1.5 text-[11px] text-white/45">{helper}</div>}
    </label>
  );
}

export function SectionTitle({ overline, title, subtitle, align = "left" }) {
  const alignCls = align === "center" ? "text-center mx-auto" : "";
  return (
    <div className={`mb-12 max-w-3xl ${alignCls}`}>
      {overline && <div className={`eyebrow mb-4 ${alignCls}`}>{overline}</div>}
      <h2 className="serif-display text-4xl md:text-5xl lg:text-6xl font-normal tracking-tight leading-[1.05] text-ivory">
        {title}
      </h2>
      {subtitle && <p className="mt-5 text-ivory-70 text-lg leading-relaxed">{subtitle}</p>}
    </div>
  );
}
